// internationalization
import { Trans, useTranslation } from 'react-i18next';

// material ui icons
import LinkIcon from '@mui/icons-material/Link';
import TrendingDownRoundedIcon from '@mui/icons-material/TrendingDownRounded';
import TrendingUpRoundedIcon from '@mui/icons-material/TrendingUpRounded';

// material ui
import { Alert, Link, Skeleton, Stack, Typography } from '@mui/material';

// shared components
import InfoTooltipIcon from '@/components/ui/icon/InfoTooltipIcon';

// components
import { formatNumber } from '@/utils/formatters';

// relative
import {
  getEnergyconsultantTypeForBbrCategory,
  getExpectedConsumptionForBbrCategory,
} from '../../../../utils/bbrBuildingUseMapping';
import { computeCDF } from './benchmark';
import type { BenchmarkAssessmentProps } from './benchmark';

const HBEMO_REFERENCE_URL =
  'https://www.hbemo.dk/vaerktoejer-og-tabeller/solcelleanlaeg/fordeling-af-el-forbrug-efter-anvendelse';

const BenchmarkAssessment = ({
  benchmarkData,
  benchmarkPDF,
  isLoading,
}: BenchmarkAssessmentProps) => {
  const { t } = useTranslation('portfolio-analysis');

  const medianDiff = benchmarkData.MedianDiff ?? 0;
  const absDiff = Math.abs(medianDiff);
  const usesLess = medianDiff < 0;

  const cdf = computeCDF(benchmarkPDF, benchmarkData.kWhPrYPrM2);
  const hasPDF = benchmarkPDF.length >= 2;
  const pctBetterThan = hasPDF ? Math.round((1 - cdf) * 100) : null;

  const diffKey = usesLess
    ? 'benchmarkChart.assessment.usesLess'
    : 'benchmarkChart.assessment.usesMore';

  const consultantCategory = getEnergyconsultantTypeForBbrCategory(
    benchmarkData.ExtractedBuildingUse,
  );
  const consultantReference = getExpectedConsumptionForBbrCategory(
    benchmarkData.ExtractedBuildingUse,
  );
  const consultantDiffAbsolute =
    consultantReference != null
      ? benchmarkData.kWhPrYPrM2 - consultantReference
      : null;
  const consultantDiffPct =
    consultantReference != null && consultantDiffAbsolute != null
      ? Math.round((consultantDiffAbsolute / consultantReference) * 100)
      : null;
  const consultantUsesLess = (consultantDiffPct ?? 0) < 0;
  const consultantDiffKey =
    consultantDiffPct != null
      ? consultantUsesLess
        ? 'benchmarkChart.assessment.consultantUsesLess'
        : 'benchmarkChart.assessment.consultantUsesMore'
      : 'benchmarkChart.assessment.consultantNoReference';

  return (
    <Stack spacing={1}>
      <Alert
        severity={usesLess ? 'success' : 'warning'}
        icon={
          usesLess ? (
            <TrendingDownRoundedIcon fontSize="inherit" />
          ) : (
            <TrendingUpRoundedIcon fontSize="inherit" />
          )
        }
        sx={{ borderRadius: 2 }}
      >
        <Stack spacing={1}>
          <Typography variant="body2">
            <Trans
              t={t}
              i18nKey={diffKey}
              values={{ diff: formatNumber(absDiff) }}
              components={{ bold: <strong /> }}
            />
          </Typography>
          <Typography variant="body2">
            {pctBetterThan != null ? (
              <Trans
                t={t}
                i18nKey="benchmarkChart.assessment.betterThan"
                values={{ pct: pctBetterThan }}
                components={{ bold: <strong /> }}
              />
            ) : (
              '-'
            )}
          </Typography>
        </Stack>
      </Alert>

      {isLoading ? (
        <Skeleton variant="rounded" height={64} sx={{ borderRadius: 2 }} />
      ) : (
        <Alert
          severity={
            consultantDiffPct == null
              ? 'info'
              : consultantUsesLess
                ? 'success'
                : 'warning'
          }
          icon={
            consultantDiffPct != null ? (
              consultantUsesLess ? (
                <TrendingDownRoundedIcon fontSize="inherit" />
              ) : (
                <TrendingUpRoundedIcon fontSize="inherit" />
              )
            ) : undefined
          }
          sx={{ borderRadius: 2 }}
        >
          <Stack direction="row" alignItems="flex-start" spacing={0.5}>
            <Typography variant="body2">
              <Trans
                t={t}
                i18nKey={consultantDiffKey}
                values={{
                  diff: formatNumber(Math.abs(consultantDiffPct ?? 0)),
                  category: consultantCategory ?? '',
                  reference: consultantReference ?? 0,
                }}
                components={{ bold: <strong /> }}
              />
            </Typography>
            {consultantDiffPct != null && (
              <InfoTooltipIcon
                title={
                  <Stack spacing={0.5}>
                    <Typography>
                      {t('benchmarkChart.assessment.consultantTooltipBody', {
                        category: consultantCategory ?? '',
                        reference: consultantReference ?? 0,
                      })}
                    </Typography>
                    <Link
                      href={HBEMO_REFERENCE_URL}
                      target="_blank"
                      rel="noopener noreferrer"
                      color="inherit"
                      sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
                    >
                      <LinkIcon fontSize="inherit" />
                      {t('benchmarkChart.assessment.consultantSourceLink')}
                    </Link>
                  </Stack>
                }
              />
            )}
          </Stack>
        </Alert>
      )}
    </Stack>
  );
};

export default BenchmarkAssessment;
